/**
 * LinkLine component for Infrastructure Map
 *
 * Renders a connection line between two endpoints with styling based on link status:
 * - Confirmed: Solid green line
 * - Suspected: Solid red line
 * - Open Question: Dashed yellow line with optional speech bubble
 */

import { useTranslation } from 'react-i18next'
import type { CanvasPosition, LinkStatus } from '../../../types/infrastructure-map'

interface LinkLineProps {
  sourcePosition: CanvasPosition
  targetPosition: CanvasPosition
  status: LinkStatus
  hasOpenQuestion?: boolean
  isSelected?: boolean
  onClick?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
  onHover?: (hovered: boolean) => void
}

const statusStyles: Record<LinkStatus, { stroke: string; strokeDark: string }> = {
  confirmed: {
    stroke: 'rgb(16, 185, 129)',
    strokeDark: 'rgb(52, 211, 153)',
  },
  suspected: {
    stroke: 'rgb(239, 68, 68)',
    strokeDark: 'rgb(248, 113, 113)',
  },
  'open-question': {
    stroke: 'rgb(251, 191, 36)',
    strokeDark: 'rgb(252, 211, 77)',
  },
}

const legendI18nKeys: Record<LinkStatus, string> = {
  confirmed: 'infrastructure.canvas.linkStatusConfirmed',
  suspected: 'infrastructure.canvas.linkStatusSuspected',
  'open-question': 'infrastructure.canvas.linkStatusOpenQuestion',
}

const CURVE_FACTOR = 0.15
const MAX_CURVE = 25
const ARROW_LENGTH = 10
const BUBBLE_OFFSET_Y = 20
const ARROW_HALF_ANGLE_DIVISOR = 6
const ARROW_HALF_ANGLE = Math.PI / ARROW_HALF_ANGLE_DIVISOR
const QUARTER_WEIGHT = 0.25
const HALF_WEIGHT = 0.5
const HALF_OPACITY = 0.5
const FADED_OPACITY = 0.7
const SELECTED_STROKE_WIDTH = 3
const DEFAULT_STROKE_WIDTH = 2

interface LinkGeometry {
  pathD: string
  arrowPoints: string
  curveMidX: number
  curveMidY: number
}

function computeLinkGeometry(source: CanvasPosition, target: CanvasPosition): LinkGeometry {
  const midX = (source.x + target.x) / 2
  const midY = (source.y + target.y) / 2
  const dx = target.x - source.x
  const dy = target.y - source.y
  const distance = Math.hypot(dx, dy)
  const curveAmount = Math.min(distance * CURVE_FACTOR, MAX_CURVE)
  const perpX = distance > 0 ? -dy / distance : 0
  const perpY = distance > 0 ? dx / distance : 0
  const controlX = midX + perpX * curveAmount
  const controlY = midY + perpY * curveAmount
  const pathD = `M ${String(source.x)} ${String(source.y)} Q ${String(controlX)} ${String(controlY)} ${String(target.x)} ${String(target.y)}`

  const arrowAngle = Math.atan2(target.y - controlY, target.x - controlX)
  const arrowX1 = target.x - ARROW_LENGTH * Math.cos(arrowAngle - ARROW_HALF_ANGLE)
  const arrowY1 = target.y - ARROW_LENGTH * Math.sin(arrowAngle - ARROW_HALF_ANGLE)
  const arrowX2 = target.x - ARROW_LENGTH * Math.cos(arrowAngle + ARROW_HALF_ANGLE)
  const arrowY2 = target.y - ARROW_LENGTH * Math.sin(arrowAngle + ARROW_HALF_ANGLE)
  const arrowPoints = `${String(target.x)},${String(target.y)} ${String(arrowX1)},${String(arrowY1)} ${String(arrowX2)},${String(arrowY2)}`

  const curveMidX = QUARTER_WEIGHT * source.x + HALF_WEIGHT * controlX + QUARTER_WEIGHT * target.x
  const curveMidY = QUARTER_WEIGHT * source.y + HALF_WEIGHT * controlY + QUARTER_WEIGHT * target.y

  return { pathD, arrowPoints, curveMidX, curveMidY }
}

interface MidpointDotProps {
  x: number
  y: number
  fill: string
  isSelected: boolean
  onClick: (() => void) | undefined
  onContextMenu: ((e: React.MouseEvent) => void) | undefined
}

function MidpointDot({
  x,
  y,
  fill,
  isSelected,
  onClick,
  onContextMenu,
}: MidpointDotProps): React.ReactElement {
  return (
    <g onClick={onClick} onContextMenu={onContextMenu} style={{ cursor: 'pointer' }}>
      <circle cx={x} cy={y} r={12} fill="transparent" style={{ pointerEvents: 'all' }} />
      <circle
        cx={x}
        cy={y}
        r={5}
        fill={fill}
        fillOpacity={isSelected ? 1 : HALF_OPACITY}
        stroke="white"
        strokeWidth={1.5}
        className="transition-all duration-150"
      />
    </g>
  )
}

interface OpenQuestionBubbleProps {
  x: number
  y: number
  onClick: (() => void) | undefined
  onContextMenu: ((e: React.MouseEvent) => void) | undefined
}

function OpenQuestionBubble({
  x,
  y,
  onClick,
  onContextMenu,
}: OpenQuestionBubbleProps): React.ReactElement {
  const handleClick = (e: React.MouseEvent<SVGGElement>): void => {
    e.stopPropagation()
    onClick?.()
  }
  const handleContextMenu = (e: React.MouseEvent<SVGGElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    onContextMenu?.(e)
  }

  return (
    <g
      transform={`translate(${String(x)}, ${String(y - BUBBLE_OFFSET_Y)})`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={-20}
        y={-18}
        width={40}
        height={48}
        fill="transparent"
        style={{ pointerEvents: 'all' }}
      />
      <rect
        x={-14}
        y={-12}
        width={28}
        height={24}
        rx={5}
        fill="rgb(251, 191, 36)"
        stroke="rgb(245, 158, 11)"
        strokeWidth={1.5}
        className="transition-all duration-150"
        style={{ pointerEvents: 'all' }}
      />
      <text
        x={0}
        y={6}
        textAnchor="middle"
        fontSize={16}
        fontWeight="bold"
        fill="white"
        style={{ pointerEvents: 'none' }}
      >
        ?
      </text>
      <polygon points="0,12 -5,20 5,20" fill="rgb(251, 191, 36)" style={{ pointerEvents: 'all' }} />
    </g>
  )
}

interface LinkPathsProps {
  pathD: string
  style: { stroke: string; strokeDark: string }
  isSelected: boolean
  isDashed: boolean
  isInteractive: boolean
  onClick: (() => void) | undefined
  onContextMenu: ((e: React.MouseEvent) => void) | undefined
}

function LinkPaths({
  pathD,
  style,
  isSelected,
  isDashed,
  isInteractive,
  onClick,
  onContextMenu,
}: LinkPathsProps): React.ReactElement {
  const dashArray = isDashed ? '8,4' : 'none'
  return (
    <>
      {isInteractive && (
        <path
          d={pathD}
          fill="none"
          stroke="transparent"
          strokeWidth={24}
          onClick={onClick}
          onContextMenu={onContextMenu}
          style={{ cursor: 'pointer' }}
        />
      )}
      <path
        d={pathD}
        fill="none"
        stroke={style.stroke}
        strokeWidth={isSelected ? SELECTED_STROKE_WIDTH : DEFAULT_STROKE_WIDTH}
        strokeOpacity={isSelected ? 1 : FADED_OPACITY}
        strokeDasharray={dashArray}
        className="transition-all duration-150"
        onClick={onClick}
        onContextMenu={onContextMenu}
        style={isInteractive ? { cursor: 'pointer' } : undefined}
      />
      {isSelected && (
        <path
          d={pathD}
          fill="none"
          stroke={style.strokeDark}
          strokeWidth={6}
          strokeOpacity={0.3}
          strokeDasharray={dashArray}
        />
      )}
    </>
  )
}

interface LinkAdornmentsProps {
  arrowPoints: string
  curveMidX: number
  curveMidY: number
  style: { stroke: string; strokeDark: string }
  isSelected: boolean
  showArrow: boolean
  isOpenQuestion: boolean
  isInteractive: boolean
  onClick: (() => void) | undefined
  onContextMenu: ((e: React.MouseEvent) => void) | undefined
}

function LinkAdornments({
  arrowPoints,
  curveMidX,
  curveMidY,
  style,
  isSelected,
  showArrow,
  isOpenQuestion,
  isInteractive,
  onClick,
  onContextMenu,
}: LinkAdornmentsProps): React.ReactElement {
  return (
    <>
      {showArrow && (
        <polygon
          points={arrowPoints}
          fill={style.stroke}
          fillOpacity={isSelected ? 1 : FADED_OPACITY}
        />
      )}
      {isInteractive && !isOpenQuestion && (
        <MidpointDot
          x={curveMidX}
          y={curveMidY}
          fill={style.stroke}
          isSelected={isSelected}
          onClick={onClick}
          onContextMenu={onContextMenu}
        />
      )}
      {isOpenQuestion && (
        <OpenQuestionBubble
          x={curveMidX}
          y={curveMidY}
          onClick={onClick}
          onContextMenu={onContextMenu}
        />
      )}
    </>
  )
}

export function LinkLine({
  sourcePosition,
  targetPosition,
  status,
  isSelected = false,
  onClick,
  onContextMenu,
  onHover,
}: LinkLineProps): React.ReactElement {
  const style = statusStyles[status]
  const isOpenQuestion = status === 'open-question'
  const isInteractive = !!(onClick ?? onContextMenu)
  const { pathD, arrowPoints, curveMidX, curveMidY } = computeLinkGeometry(
    sourcePosition,
    targetPosition
  )

  return (
    <g
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      className={isInteractive ? 'cursor-pointer' : ''}
    >
      <LinkPaths
        pathD={pathD}
        style={style}
        isSelected={isSelected}
        isDashed={isOpenQuestion}
        isInteractive={isInteractive}
        onClick={onClick}
        onContextMenu={onContextMenu}
      />
      <LinkAdornments
        arrowPoints={arrowPoints}
        curveMidX={curveMidX}
        curveMidY={curveMidY}
        style={style}
        isSelected={isSelected}
        showArrow={status === 'confirmed'}
        isOpenQuestion={isOpenQuestion}
        isInteractive={isInteractive}
        onClick={onClick}
        onContextMenu={onContextMenu}
      />
    </g>
  )
}

export function LinkLegend(): React.ReactElement {
  const { t } = useTranslation('cases')
  const types: { status: LinkStatus; i18nKey: string }[] = [
    { status: 'confirmed', i18nKey: legendI18nKeys.confirmed },
    { status: 'suspected', i18nKey: legendI18nKeys.suspected },
    { status: 'open-question', i18nKey: legendI18nKeys['open-question'] },
  ]

  return (
    <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
      {types.map(({ status, i18nKey }) => (
        <div key={status} className="flex items-center gap-1.5">
          <svg width={24} height={8} className="flex-shrink-0">
            <line
              x1={0}
              y1={4}
              x2={24}
              y2={4}
              stroke={statusStyles[status].stroke}
              strokeWidth={2}
              strokeDasharray={status === 'open-question' ? '4,2' : 'none'}
            />
            {status === 'confirmed' && (
              <polygon points="24,4 18,1 18,7" fill={statusStyles[status].stroke} />
            )}
          </svg>
          <span>{t(i18nKey as never)}</span>
        </div>
      ))}
    </div>
  )
}

export default LinkLine
